// ============================================================
// home-search/utils.js — Helpers puros (sin DOM, sin side effects)
// ============================================================

import { COUNTRIES_WITH_FLAGS } from '../paises-util.js';

/**
 * Convierte URLs relativas de uploads a URLs absolutas via CDN
 */
export function withBackendOrigin(url) {
  try {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/uploads/')) {
      return `https://cdn.publikaya.com${url}`;
    }
    return url;
  } catch { return url; }
}

/**
 * Sanitiza texto para prevenir XSS
 */
export function escapeHtml(text) {
  const s = text == null ? '' : String(text);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Valida que una URL sea http/https segura
 */
export function safeUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const u = new URL(url, window.location.origin);
    const p = u.protocol.toLowerCase();
    if (p === 'http:' || p === 'https:') return u.href;
    return '';
  } catch {
    return '';
  }
}

/**
 * Normaliza texto: minúsculas + sin acentos + trim
 */
export function normalizeText(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * Detecta país por nombre o código a partir de un texto libre
 */
export function detectCountry(input) {
  const n = normalizeText(input);
  if (!n) return null;
  let found = COUNTRIES_WITH_FLAGS.find(c => normalizeText(c.name) === n);
  if (!found) {
    found = COUNTRIES_WITH_FLAGS.find(c => (c.code || '').toLowerCase() === n);
  }
  return found ? { name: found.name, code: (found.code || '').toUpperCase() } : null;
}

/**
 * Valida si un negocio coincide con un país (tolerante)
 */
export function matchesCountry(biz, pais, paisCode) {
  if (!pais && !paisCode) return true;
  const nameTarget = normalizeText(pais || '');
  const codeTarget2 = (paisCode || '').trim().toUpperCase();
  const codeTarget3 = codeTarget2.length === 2 ? codeTarget2 + 'L' : codeTarget2;

  const rawNames = [
    biz.country,
    biz.country_name,
    biz.countryName,
    biz.pais,
    biz.pais_nombre,
    biz.location && biz.location.country,
    biz.address_country,
    biz.address && biz.address.country
  ].filter(Boolean);
  const rawCodes = [
    biz.country_code,
    biz.countryCode,
    biz.location && biz.location.country_code,
    biz.address && biz.address.country_code
  ].filter(Boolean);

  const normNames = rawNames.map(v => normalizeText(String(v)));
  const upCodes = rawCodes.map(v => String(v).trim().toUpperCase());

  if (normNames.length === 0 && upCodes.length === 0) return true;

  if (nameTarget) {
    for (const n of normNames) {
      if (n === nameTarget) return true;
      if (n.includes(nameTarget)) return true;
    }
  }

  if (codeTarget2) {
    for (const c of upCodes) {
      if (c === codeTarget2) return true;
      if (c === codeTarget3) return true;
      if (c.startsWith(codeTarget2)) return true;
    }
  }

  return false;
}

/**
 * Debounce con timeout configurable (default 250ms)
 */
export function debounce(fn, delay = 250) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Convierte texto a slug URL-safe
 */
export function toSlug(str) {
  const n = normalizeText(String(str || ''));
  return n.replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

/**
 * Obtiene el target de navegación para un negocio (subcategoría > categoría)
 */
export function getBizTarget(b) {
  const sub = b.subcategory_slug || b.subcategorySlug || b.subcategory;
  if (sub) return { type: 'subcategory', slug: toSlug(sub) };
  const cat = b.category_slug || b.categorySlug || b.category;
  if (cat) return { type: 'category', slug: toSlug(cat) };
  return null;
}

/**
 * Navega a categories.html con contexto del negocio
 */
export function navigateToBizContext(b) {
  try {
    const target = getBizTarget(b);
    if (!target || !target.slug) return false;
    const param = target.type === 'subcategory' ? 'subcategory' : 'category';
    const hl = (b && (b.slug || b.id || b._id)) ? String(b.slug || b.id || b._id) : '';
    const base = `/categories.html?${param}=${encodeURIComponent(target.slug)}`;
    window.location.href = hl ? `${base}&highlight=${encodeURIComponent(hl)}` : base;
    return true;
  } catch (_) { return false; }
}

/**
 * Obtiene el valor seleccionado del filtro de categoría
 */
export function getSelectedCategoryValue() {
  const el = document.querySelector('.search-card select');
  if (!el) return '';
  const value = (el.value || '').trim();
  const lower = value.toLowerCase();
  return (lower === 'seleccionar categoria' || lower === 'seleccionar categoría') ? '' : value;
}
